import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Loader } from '../Components/Loader/Loader';

interface LoaderContextType {
  showLoader: () => void;
  hideLoader: () => void;
  setLoaderProgress: (value: number | null) => void;
}

const LoaderContext = createContext<LoaderContextType | undefined>(undefined);

export const LoaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const pendingRequestsRef = useRef(0);
  const lastHiddenAtRef = useRef<number | null>(null);
  const showTimerRef = useRef<number | null>(null);

  const clearShowTimer = useCallback(() => {
    if (!showTimerRef.current) return;
    clearTimeout(showTimerRef.current);
    showTimerRef.current = null;
  }, []);

  const showLoader = useCallback(() => {
    pendingRequestsRef.current += 1;
    if (isLoading) return;

    const now = Date.now();
    const lastHiddenAt = lastHiddenAtRef.current;
    if (lastHiddenAt && now - lastHiddenAt < 3000) {
      const delay = 3000 - (now - lastHiddenAt);
      clearShowTimer();
      showTimerRef.current = window.setTimeout(() => {
        if (pendingRequestsRef.current > 0) {
          setIsLoading(true);
        }
        showTimerRef.current = null;
      }, delay);
      return;
    }

    setIsLoading(true);
  }, [clearShowTimer, isLoading]);

  const hideLoader = useCallback(() => {
    if (pendingRequestsRef.current === 0) return;

    pendingRequestsRef.current -= 1;
    if (pendingRequestsRef.current > 0) return;

    clearShowTimer();
    setIsLoading(false);
    lastHiddenAtRef.current = Date.now();
    setProgress(null);
  }, [clearShowTimer]);

  const setLoaderProgress = useCallback((value: number | null) => {
    if (value === null) {
      setProgress(null);
      return;
    }

    const nextValue = Math.min(100, Math.max(0, Math.round(value)));
    setProgress(nextValue);
  }, []);

  return (
    <LoaderContext.Provider value={{ showLoader, hideLoader, setLoaderProgress }}>
      <Loader isVisible={isLoading} progress={progress} />
      {children}
    </LoaderContext.Provider>
  );
};

export const useLoader = () => {
  const context = useContext(LoaderContext);
  if (!context) {
    throw new Error('useLoader must be used within a LoaderProvider');
  }
  return context;
};
